import { v4 } from 'uuid'
import { Button, Tooltip, Modal } from 'antd'
import { useSnackbar } from "notistack"
import { focusHandling } from "cruip-js-toolkit"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Switch, Route, Redirect, useLocation, useHistory, Link } from "react-router-dom"

import { AiOutlineAlert, AiOutlineClose } from 'react-icons/ai'

import "./charts/ChartjsConfig"

// Import pages
import Login from './pages/Login'
import Setup from './pages/Setup'
import Consultation from './pages/Consultation/'
import Dental from './pages/Dental/'
import Optical from './pages/Optical/'
import Cashier from './pages/Cashier/'
import Inpatient from "./pages/Inpatient"
import Triage from "./pages/Triage"
import Lab from "./pages/Lab/"
// import DentalLab from "./pages/DentalLab"
// import DentalXRay from "./pages/DentalXray"
import Radiography from "./pages/Radiography/"
import Nursing from "./pages/Nursing/"
import Physiotherapy from "./pages/Physiotherapy/"
import Trauma from "./pages/Trauma/"
import Store from "./pages/Store/"
import Procurement from "./pages/Procurement/"
import Pharmacy from "./pages/Pharmacy/"
import Sales from "./pages/Sales"
import Theatre from "./pages/Theatre/"
import Sonography from "./pages/Sonography/"
import CreditPatientDocs from "./pages/CreditPatientDocs"
import CreditControl from "./pages/CreditControl"
import Records from "./pages/Records/"
import FrontDesk from "./pages/Front-Desk/"
import Documents from "./pages/Documents/"

import Reports from "./pages/Reports/"
import Admin from "./pages/Admin/"
import Accounts from "./pages/Accounts/"
import HR from "./pages/HR/"
import Marketing from "./pages/Marketing"
import StockTake from "./pages/Stock-Take"

// Import partial components
import Sidebar from './partials/Sidebar'
import Header from './partials/Header'
import Receive from './components/reusable/receive'

import LockSession from "./components/modals/lockSession"

import { Close, notifOps, usePet, useToggle, ws, normalizeLinkCase, useSocketEvents, useCodeBlue } from "./modules"

import { logout, setCodeBlue, setInventoryAlert, setparams, setShift, setuser } from "./store/actions"

import "react-datepicker/dist/react-datepicker.css"
import Confirm from "./components/reusable/confirm"
import CodeBlue from "./components/modals/codeBlue"
import Settings from './components/modals/settings'

let code = v4()

const Router = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const history = useHistory()

    const dispatch = useDispatch()

    const { user } = useSelector(state => state.application)

    useEffect(() => {
        // user is populated by a separate dispatch(setuser(...)) right after dispatch(login(...))
        // (unbatched, since both fire inside a setTimeout) - Router can mount and run this effect
        // in the gap between the two, while user is still null. Bailing out here instead of
        // falling through to dispatch(logout()) avoids wiping the token that was just set.
        if (!user) return

        if (user.stations.length === 0 && !user.issuperuser) {
            enqueueSnackbar('Please contact admin to assign you to your designated stations', notifOps('warning', <Close onClick={closeSnackbar} />, true))
        } else if (user.stations.length > 0 || user.issuperuser) {
            history.push(`/app/${user.stations[0]?.name ?? 'admin'}`)
            return
        }

        dispatch(logout())
    }, [user]);


    return null
}

const Protected = ({ station, children }) => {
    const { user } = useSelector(state => state.application)
    const allowed = user?.issuperuser || user?.stations?.some(s => s.name === station)

    if (!allowed) return <Redirect to="/app" />

    return children
}

const Main = () => {
    const [menu, toggleMenu] = useToggle()

    const [lock, setLock] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [subscriptionBlocked, setSubscriptionBlocked] = useState(false)

    const { params, user } = useSelector(state => state.application)

    const dispatch = useDispatch()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    let timer, int, num = 0

    const clearShift = () => {
        toggleMenu()
        dispatch(setShift())
    }

    const lockSession = () => {
        // console.log("locking")
        setLock(true)
        clearTimeout(timer)
        // clearInterval(int)
        // window.onload = null
        // document.onmousemove = null

        // remove this after 
        document.removeEventListener("click", resetTimer);
        document.removeEventListener("keydown", keydown);

        sessionStorage.removeItem('token')
    }

    const resetTimer = () => {
        // console.log("reset")
        setLock(false)
        num = 0
        clearTimeout(timer)
        // clearInterval(int)
        // timer = setTimeout(lockSession, (params?.timeOut ? params.timeOut : 20) * 60000)
        // timer = setTimeout(lockSession, 15000)
        timer = setTimeout(lockSession, (params?.timeOut ?? 20) * 60000)
        // int = setInterval(() => {
        //     console.log('second: ', num)
        //     num++
        // }, 1000)

        // window.onload = resetTimer
    }

    const keydown = e => {
        // Enters here 
        if (e.altKey && e.key === 'l') {
            // this is not called
            lockSession()
        } else {
            resetTimer()
        }
    }

    const onComplete = ({ data }) => {
        ws.send('gotMe', data._id)

        dispatch(setuser(data))

        resetTimer()

        // document.onmousemove = resetTimer
        // document.onclick = resetTimer
        document.addEventListener("click", resetTimer)
        document.addEventListener("keydown", keydown) // Pending not workin
        // document.onkeypress = keydown
    }

    useSocketEvents({
        'new-system-update': {
            condition: () => true,
            notif: {
                message: 'New System Update',
                type: 'info'
            },
            callback: () => {
                let stat = alert('New System Update! Proceed to Refresh.')
                console.log(stat)
                if (stat)
                    window.location.reload()
            }
        },

        'kick-out': {
            condition: data => data.user === user?._id,
            notif: {
                message: 'You have been logged out by system admin',
                type: 'info'
            },
            callback: () => dispatch(logout())
        },

        'low-stock-alert': {
            condition: data => user?.issuperuser || user?.stores?.some(st => (st?.name ?? st?.store?.name) === data.destination),
            notif: {
                message: data => `Low stock: ${data.destination} (${data.items?.length ?? 0} item(s))`,
                type: 'warning'
            },
            callback: data => dispatch(setInventoryAlert({ ...data, type: 'low-stock-alert' }))
        },

        'near-expiry-alert': {
            condition: data => user?.issuperuser || user?.stores?.some(st => (st?.name ?? st?.store?.name) === data.destination),
            notif: {
                message: data => `Near expiry: ${data.destination} (${data.items?.length ?? 0} batch(es))`,
                type: 'warning'
            },
            callback: data => dispatch(setInventoryAlert({ ...data, type: 'near-expiry-alert' }))
        },

        // Generic, cross-module notification delivery - any module that calls
        // createNotification() lands here, not just inventory. No toast here since a module
        // creating a notification may already show its own (as low-stock-alert/near-expiry-alert
        // do above) - this only needs to tell the bell (Notifications.js) to refetch its
        // unread count/list, reusing lastInventoryAlert as a generic "something changed" signal.
        'notification': {
            condition: data => data.recipients?.includes(String(user?._id)),
            callback: data => dispatch(setInventoryAlert({ ...data, type: 'notification' }))
        }
    })

    const [{ loading, error }] = usePet('me', {}, { notify: false, callback: onComplete, logOutOnError: false })

    useEffect(() => {
        if (!error?.response) return
        let payload = error.response.data
        if (typeof payload === 'string') {
            try {
                payload = JSON.parse(payload)
            } catch (_) {
                enqueueSnackbar('Could not load your profile', notifOps('warning', <Close onClick={closeSnackbar} />, false))
                return
            }
        }
        const status = error.response.status
        if (status === 402 || payload?.errorCode === 'SUBSCRIPTION_INACTIVE') {
            setSubscriptionBlocked(true)
            return
        }
        if (status === 401) {
            dispatch(logout())
            return
        }
        enqueueSnackbar(payload?.message || 'Could not load your profile', notifOps('warning', <Close onClick={closeSnackbar} />, false))
    }, [error])

    const receiveError = error && !subscriptionBlocked

    return (
        <>
        <Modal
            title="Subscription inactive"
            visible={subscriptionBlocked}
            closable={false}
            maskClosable={false}
            footer={[
                <Button
                    key="signout"
                    type="primary"
                    onClick={() => {
                        setSubscriptionBlocked(false)
                        dispatch(logout())
                    }}
                >
                    Sign out
                </Button>,
            ]}
        >
            <p className="text-gray-700">
                Your organization&apos;s subscription is not active. Please complete payment or contact your administrator
                to restore access.
            </p>
        </Modal>
        <Receive
            error={receiveError}
            loading={loading}
            progress>
            <div className="flex h-screen overflow-hidden">
                {user?.stations?.length > 1 && <Sidebar stations={user?.stations} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />}
                <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                    <Header stations={user?.stations} lockSession={lockSession} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                    <main style={{ height: '90%' }}>
                        <Switch>
                            <Route exact path="/app">
                                <Router />
                            </Route>
                            <Route path="/app/cashier">
                                <Protected station="cashier"><Cashier /></Protected>
                            </Route>
                            <Route path="/app/front-desk">
                                <Protected station="front-desk"><FrontDesk /></Protected>
                            </Route>
                            <Route path="/app/consultation">
                                <Protected station="consultation"><Consultation /></Protected>
                            </Route>
                            <Route path="/app/triage">
                                <Protected station="triage"><Triage /></Protected>
                            </Route>
                            <Route path="/app/lab">
                                <Protected station="lab"><Lab /></Protected>
                            </Route>
                            <Route path="/app/dental">
                                <Protected station="dental"><Dental /></Protected>
                            </Route>
                            <Route path="/app/optical">
                                <Protected station="optical"><Optical /></Protected>
                            </Route>
                            {/* <Route path="/app/dental-lab">
                                <DentalLab />
                            </Route>
                            <Route path="/app/dental-xray">
                                <DentalXRay />
                            </Route> */}
                            <Route path="/app/radiography">
                                <Protected station="radiography"><Radiography /></Protected>
                            </Route>
                            <Route path="/app/nursing">
                                <Protected station="nursing"><Nursing /></Protected>
                            </Route>
                            <Route path="/app/records">
                                <Protected station="records"><Records /></Protected>
                            </Route>
                            <Route path="/app/physiotherapy">
                                <Protected station="physiotherapy"><Physiotherapy /></Protected>
                            </Route>
                            <Route path="/app/trauma">
                                <Protected station="trauma"><Trauma /></Protected>
                            </Route>
                            <Route path="/app/theatre">
                                <Protected station="theatre"><Theatre /></Protected>
                            </Route>
                            <Route path="/app/sonography">
                                <Protected station="sonography"><Sonography /></Protected>
                            </Route>
                            <Route path="/app/credit-control">
                                <Protected station="credit-control"><CreditControl /></Protected>
                            </Route>
                            <Route path="/app/credit-patient-docs">
                                <Protected station="credit-patient-docs"><CreditPatientDocs /></Protected>
                            </Route>
                            <Route path="/app/pharmacy">
                                <Protected station="pharmacy"><Pharmacy /></Protected>
                            </Route>
                            <Route path="/app/inpatient">
                                <Protected station="inpatient"><Inpatient /></Protected>
                            </Route>
                            <Route path="/app/store">
                                <Protected station="store"><Store /></Protected>
                            </Route>
                            <Route path="/app/procurement">
                                <Protected station="procurement"><Procurement /></Protected>
                            </Route>
                            <Route path="/app/sales">
                                <Protected station="sales"><Sales /></Protected>
                            </Route>
                            <Route path="/app/documents">
                                <Protected station="documents"><Documents /></Protected>
                            </Route>
                            <Route path="/app/human-resources">
                                <Protected station="human-resources"><HR /></Protected>
                            </Route>
                            <Route path="/app/stock-take">
                                <Protected station="stock-take"><StockTake /></Protected>
                            </Route>
                            <Route path="/app/accounts">
                                <Protected station="accounts"><Accounts /></Protected>
                            </Route>
                            <Route path="/app/reports">
                                <Protected station="reports"><Reports /></Protected>
                            </Route>
                            <Route path="/app/admin">
                                <Protected station="admin"><Admin /></Protected>
                            </Route>
                            {/* <Route exact path="/app/users">
                                <Users />
                            </Route>
                            <Route exact path="/app/received-invoices">
                                <Invoices />
                            </Route>
                            <Route exact path="/app/invoices">
                                <OutInvoices />
                            </Route>
                            <Route exact path="/app/sales">
                                <SalesByItem />
                            </Route>
                            <Route exact path="/app/inventory">
                                <Inventory />
                            </Route> 
                            <Route exact path='/app/inventory/:id/history'>
                                <ItemTimeline />
                            </Route> */}
                        </Switch>
                    </main>
                </div>
                {/* {
                    user?.stations?.length > 1 &&
                    <Popover
                        placement="topRight"
                        title="Menu" 
                        content={
                            <Menu>
                                {
                                    user?.stations.map((station, i) =>
                                        <Link to={ `/app/${station?.name}` }>
                                            <Menu.Item  key={ i } onClick={ clearShift }>{ normalizeLinkCase(station.name.replaceAll('-', ' ')) }</Menu.Item>
                                        </Link>
                                    )
                                }
                            </Menu>
                        } 
                        onVisibleChange={ toggleMenu }
                        visible={menu}
                        trigger="click">
                        <span className="fixed bottom-5 right-10">
                            <button className="hisani-btn-green p-0 text-white flex justify-center items-center font-extrabold w-12 h-12 rounded-full active:shadow-lg mouse shadow transition ease-in duration-200 focus:outline-none">
                                { menu ? <AiOutlineClose /> : <AiOutlineMenu /> }
                            </button>
                        </span>
                    </Popover>
                } */}
            </div>
            {

            }
            {
                lock &&
                <LockSession
                    lockSession={lockSession}
                    onPerform={resetTimer}
                    visible={lock} />
            }
        </Receive>
        </>
    )
}

const App = () => {
    const [activeCodeBlue, setActiveCodeBlue] = useState(false)

    const location = useLocation()
    const history = useHistory()

    const { loggedIn, codeBlue } = useSelector(state => state.application)

    const [setupState, setSetupState] = useState({ completed: null, hospitalName: null, country: null })
    const [settings, toggleSettings] = useToggle(localStorage.getItem('station') ? false : true)
    
    const dispatch = useDispatch()
    
    useCodeBlue(code)

    const [, triggerCodeBlue] = usePet('triggerCodeBlue', {
        code,
        station: localStorage.getItem('station')
    }, { notify: true, callback: (_, notify, ops) => {
        setActiveCodeBlue(true)
        notify('Code Blue Alert Triggered', ops('success'))
    }, manual: true, load: { msg: 'Triggering Alert...' } })

    const [, stopCodeBlue] = usePet('stopCodeBlue', null, { notify: true, callback: (_, notify, ops) => {
        setActiveCodeBlue(false)
        notify('Code Blue Alert Stopped', ops('success'))
    }, manual: true, load: { msg: 'Stopping Alert...' } })

    const [{ loading, error }, loadParams] = usePet('params', {}, {
        notify: true, manual: true, callback: ({ data }) => {
            if (data?.name) {
                document.title = `Hisani for ${data?.name}`
                dispatch(setparams(data))
            }
        }, logOutOnError: true
    })

    const [{ loading: setupLoading, error: setupError }, loadSetupStatus] = usePet('setupStatus', {}, {
        bypassLock: true,
        notify: false,
        manual: true,
        callback: ({ data }) => {
            setSetupState({
                completed: data?.completed,
                hospitalName: data?.hospitalName ?? null,
                country: data?.country ?? null
            })
        },
        logOutOnError: false
    })

    useEffect(() => {
        loadParams()
        loadSetupStatus()
        // usePet returns fresh execute functions each render; this should only run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (setupState.completed === false && location.pathname !== '/setup') {
            history.replace('/setup')
        }

        if (setupState.completed === true && location.pathname === '/setup') {
            history.replace('/login')
        }
    }, [setupState.completed, location.pathname, history])

    useEffect(() => {
        document.querySelector('html').style.scrollBehavior = 'auto'
        window.scroll({ top: 0 })
        document.querySelector('html').style.scrollBehavior = ''
        // focusHandling('outline')
    }, [location.pathname])

    const confirmCodeBlue = () => Confirm({
        title: activeCodeBlue ? 'Cancel Code Blue Alert?' : 'Send Code Blue Alert?',
        message: activeCodeBlue ? 'Are you sure you want to cancel the code blue alert?' : 'This will alert all stations in the facility',
        okText: activeCodeBlue ? 'Cancel Alert' : 'Send Alert',
        onOk: activeCodeBlue ? stopCodeBlue : triggerCodeBlue
    })

    return (
        <>
            <Receive
                error={error || setupError}
                loading={loading || setupLoading || setupState.completed === null}
                progress>
                <Switch>
                    <Route exact path="/">
                        <Redirect to={ setupState.completed === false ? "/setup" : "/login" } />
                    </Route>
                    <Route path="/setup">
                        {
                            setupState.completed === true ?
                                <Redirect to="/login" />
                                : <Setup onComplete={ () => {
                                    loadSetupStatus()
                                    loadParams()
                                } } />
                        }
                    </Route>
                    <Route path="/login">
                        {
                            setupState.completed === false ?
                                <Redirect to="/setup" />
                                : <Login />
                        }
                    </Route>
                    <Route path="/app">
                        {
                            setupState.completed === false ?
                                <Redirect to="/setup" />
                                : loggedIn ?
                                <Main />
                                : <Redirect to="/login" />
                        }
                    </Route>
                </Switch>
                <CodeBlue alert={ codeBlue } />
                {
                    setupState.completed && localStorage.getItem('station') &&
                    <Tooltip title={ `${ activeCodeBlue ? 'Cancel' : 'Trigger' } Code Blue Alert` } placement="left">
                        <Button 
                            onClick={ confirmCodeBlue }
                            shape="circle" 
                            type="primary" 
                            style={{ height: '3.5rem', width: '3.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'absolute'  }}
                            className="cursor-pointer right-10 absolute bottom-8" 
                            icon={ activeCodeBlue ? <AiOutlineClose className="h-10 w-8" /> : <AiOutlineAlert className="h-10 w-8" /> } />
                    </Tooltip>
                }
            </Receive>
            {
                setupState.completed &&
                <Settings
                    toggle={ toggleSettings }
                    visible={ settings } />
            }
        </>
    )
}

export default App
